import { type ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Bot, Pause, Play, RefreshCw, Save, Trash2 } from "lucide-react";
import {
  type ChannelDetail,
  type ChannelSettings,
  type ChannelSummary,
  type DryRun,
  channelState,
  sameSettings,
  settingsProblem,
  useBots,
  useChannel,
  useChannelStats,
  useCheckChannel,
  useCopySettings,
  byPublisher,
  problemKeys,
  useGiveBot,
  useUpdateChannel,
} from "@/lib/aggregator";
import { formatAccountNo } from "@/lib/accounts";
import { formatMoment, formatNumber, formatRelative } from "@/lib/format";
import { pushToast } from "@/components/Toaster";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/dialog";
import { DetailDrawer } from "@/components/ui/drawer";
import { Label } from "@/components/ui/label";
import { SimpleSelect } from "@/components/ui/select";
import { cn } from "@/lib/cn";
import { FeedTab } from "./FeedTab";
import { FillSection } from "./FillPanel";
import { PostsTab } from "./PostsTab";
import { ProcessingTab, ScheduleTab } from "./SettingsTabs";
import { SourcesTab } from "./SourcesTab";
import { errorText, reasonText } from "./helpers";
import { Section } from "./parts";

/**
 * A channel's card: how it is doing, where it is filled from, what is done to
 * a post on the way, when posts go out — and the posts themselves: queued,
 * waiting for a person, and the record of everything that was read.
 */
export function ChannelDrawer({
  id,
  open,
  onOpenChange,
  onDelete,
  canEdit,
  channels,
  onOpenConnections,
}: {
  id: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDelete: (channel: ChannelDetail) => void;
  canEdit: boolean;
  /** The company's channels — for "the same settings as…". */
  channels: ChannelSummary[];
  onOpenConnections: (tab: "bots" | "ai") => void;
}) {
  const { t } = useTranslation();
  const detail = useChannel(open ? id : null);
  const update = useUpdateChannel();
  const [tab, setTab] = useState("overview");
  const [draft, setDraft] = useState<ChannelSettings | null>(null);
  const [leaving, setLeaving] = useState(false);
  // What the tabs are in the middle of: a tab's content is taken off the page
  // when another is opened, and these must not go with it.
  const [sourcesText, setSourcesText] = useState("");
  const [trial, setTrial] = useState<DryRun | null>(null);

  const channel = detail.data;
  // Which tab a card opens on is decided once its channel is known: one with
  // posts opens on its feed, a new one on its overview, where it is set up.
  const [opened, setOpened] = useState<string | null>(null);
  useEffect(() => {
    if (open) setTab("overview");
    setOpened(null);
    setDraft(null);
    setSourcesText("");
    setTrial(null);
  }, [open, id]);
  useEffect(() => {
    if (!open || !channel || opened === channel.id) return;
    setOpened(channel.id);
    if (channel.last_post_at || channel.queued + channel.pending > 0) setTab("feed");
  }, [open, channel, opened]);

  const settings = draft ?? channel?.settings ?? null;
  const dirty = draft !== null && channel !== undefined && !sameSettings(draft, channel.settings);
  // An edit taken back leaves no draft behind. Kept — equal to what is saved,
  // so "nothing to save" — it stood between the form and the channel: settings
  // copied from another channel showed as not copied, and the next "Save"
  // wrote the old ones back over them.
  const edit = (next: ChannelSettings) =>
    setDraft(channel && sameSettings(next, channel.settings) ? null : next);

  // The card asks before it closes on unsaved settings; the tab, closed or
  // reloaded, would not.
  useEffect(() => {
    if (!dirty) return;
    const warn = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const save = async () => {
    if (!channel || !draft) return;
    // What the server would refuse is said here, by the thing itself — and
    // the person is taken to it, not left with an answer about the whole form.
    const problem = settingsProblem(draft);
    if (problem) {
      setTab(problem === "slot" || problem === "views" ? "schedule" : "processing");
      pushToast({ kind: "error", text: t(`aggregator.settings.incomplete_${problem}`) });
      return;
    }
    try {
      await update.mutateAsync({ id: channel.id, settings: draft });
      setDraft(null);
      pushToast({ kind: "success", text: t("aggregator.settings.saved") });
    } catch (err) {
      pushToast({ kind: "error", text: errorText(err, t("aggregator.action_error")) });
    }
  };

  const setStatus = async (status: "active" | "paused") => {
    if (!channel) return;
    try {
      await update.mutateAsync({ id: channel.id, status });
    } catch (err) {
      pushToast({ kind: "error", text: errorText(err, t("aggregator.action_error")) });
    }
  };

  // Closing with unsaved settings asks first: a rule typed in for five
  // minutes and lost to a click outside is the kind of thing nobody forgives.
  const requestClose = (next: boolean) => {
    if (!next && dirty) setLeaving(true);
    else onOpenChange(next);
  };

  const body = (render: (channel: ChannelDetail, settings: ChannelSettings) => ReactNode) =>
    channel && settings ? (
      render(channel, settings)
    ) : (
      <p className="text-sm text-fg-muted">
        {t(detail.isError ? "aggregator.load_error" : "aggregator.loading")}
      </p>
    );

  const count = (n: number | undefined) =>
    n ? <span className="font-mono text-xs text-fg-muted">{n}</span> : null;

  return (
    <>
      <DetailDrawer
        open={open}
        onOpenChange={requestClose}
        title={channel?.title ?? "…"}
        status={
          channel
            ? {
                domain: "aggregator",
                value: channelState(channel),
                label: t(`aggregator.state.${channelState(channel)}`),
              }
            : undefined
        }
        activeTab={tab}
        onTabChange={(next) => {
          setTab(next);
          // A tab picked by hand before the channel came is not taken back
          // by the one the channel would open on.
          if (id) setOpened(id);
        }}
        tabs={[
          {
            value: "feed",
            label: t("aggregator.tab.feed"),
            content: body((c) => (
              <FeedTab channel={c} active={tab === "feed"} canEdit={canEdit} onOpenTab={setTab} />
            )),
          },
          {
            value: "overview",
            label: t("aggregator.tab.overview"),
            content: body((c) => (
              <Overview
                channel={c}
                channels={channels}
                canEdit={canEdit}
                onOpenTab={setTab}
                onOpenConnections={onOpenConnections}
                // With settings typed in and not saved the switch waits, as
                // the one in the footer does: the channel would start under
                // the old ones.
                onSwitchOn={dirty ? undefined : () => setStatus("active")}
                switching={update.isPending}
                dirty={dirty}
              />
            )),
          },
          {
            value: "sources",
            label: (
              <>
                {t("aggregator.tab.sources")}
                {count(channel?.sources)}
              </>
            ),
            content: body((c) => (
              <SourcesTab
                channel={c}
                canEdit={canEdit}
                text={sourcesText}
                setText={setSourcesText}
              />
            )),
          },
          {
            value: "processing",
            label: t("aggregator.tab.processing"),
            content: body((c, s) => (
              <ProcessingTab
                channel={c}
                draft={s}
                onChange={edit}
                canEdit={canEdit}
                onOpenConnections={() => onOpenConnections("ai")}
                trial={trial}
                onTrial={setTrial}
              />
            )),
          },
          {
            value: "schedule",
            label: t("aggregator.tab.schedule"),
            content: body((c, s) => (
              <ScheduleTab
                channel={c}
                draft={s}
                onChange={edit}
                canEdit={canEdit}
                onOpenConnections={() => onOpenConnections("ai")}
              />
            )),
          },
          {
            value: "queue",
            label: (
              <>
                {t("aggregator.tab.queue")}
                {count(channel ? channel.queued + channel.pending : undefined)}
              </>
            ),
            content: body((c) => (
              <PostsTab channel={c} view="queue" active={tab === "queue"} canEdit={canEdit} />
            )),
          },
          {
            value: "review",
            label: (
              <>
                {t("aggregator.tab.review")}
                {count(channel?.review)}
              </>
            ),
            content: body((c) => (
              <PostsTab channel={c} view="review" active={tab === "review"} canEdit={canEdit} />
            )),
          },
          {
            value: "journal",
            label: t("aggregator.tab.journal"),
            content: body((c) => (
              <PostsTab channel={c} view="journal" active={tab === "journal"} canEdit={canEdit} />
            )),
          },
        ]}
        footer={
          channel &&
          canEdit && (
            <>
              <Button
                variant="ghost"
                className="mr-auto text-danger hover:text-danger"
                onClick={() => onDelete(channel)}
                iconLeft={<Trash2 size={15} strokeWidth={2} aria-hidden="true" />}
              >
                {t("aggregator.channel.remove")}
              </Button>
              {dirty && (
                <>
                  <Button variant="outline" onClick={() => setDraft(null)}>
                    {t("aggregator.settings.discard")}
                  </Button>
                  <Button
                    onClick={save}
                    loading={update.isPending}
                    iconLeft={<Save size={15} strokeWidth={2} aria-hidden="true" />}
                  >
                    {t("aggregator.settings.save")}
                  </Button>
                </>
              )}
              {!dirty &&
                (channel.status === "active" ? (
                  <Button
                    variant="outline"
                    loading={update.isPending}
                    onClick={() => setStatus("paused")}
                    iconLeft={<Pause size={15} strokeWidth={2} aria-hidden="true" />}
                  >
                    {t("aggregator.channel.pause")}
                  </Button>
                ) : (
                  <Button
                    loading={update.isPending}
                    onClick={() => setStatus("active")}
                    iconLeft={<Play size={15} strokeWidth={2} aria-hidden="true" />}
                  >
                    {t("aggregator.channel.resume")}
                  </Button>
                ))}
            </>
          )
        }
      />
      <ConfirmDialog
        open={leaving}
        onOpenChange={setLeaving}
        tone="danger"
        title={t("aggregator.settings.leave_title")}
        description={t("aggregator.settings.leave_hint")}
        confirmText={t("aggregator.settings.leave")}
        cancelText={t("aggregator.settings.stay")}
        onConfirm={() => {
          setDraft(null);
          onOpenChange(false);
        }}
      />
    </>
  );
}
ChannelDrawer.displayName = "ChannelDrawer";

/* ---------------------------------------------------------------- overview */

function Tile({
  label,
  value,
  note,
  tone,
  onClick,
}: {
  label: ReactNode;
  value: ReactNode;
  note?: ReactNode;
  tone?: "warning" | "danger";
  /** Every number opens the tab that explains it. */
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "rounded-xl border border-border bg-bg-primary px-4 py-3 text-left transition-colors",
        "hover:border-fg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent",
      )}
    >
      <div className="text-xs text-fg-muted">{label}</div>
      <div
        className={cn(
          "mt-1 font-mono text-2xl font-semibold tabular-nums leading-none",
          tone === "warning" && "text-warning",
          tone === "danger" && "text-danger",
        )}
      >
        {value}
      </div>
      {note && <div className="mt-1.5 text-xs text-fg-muted">{note}</div>}
    </button>
  );
}

function Overview({
  channel,
  channels,
  canEdit,
  onOpenTab,
  onOpenConnections,
  onSwitchOn,
  switching,
  dirty,
}: {
  channel: ChannelDetail;
  channels: ChannelSummary[];
  canEdit: boolean;
  onOpenTab: (tab: string) => void;
  onOpenConnections: (tab: "bots" | "ai") => void;
  onSwitchOn?: () => void;
  switching: boolean;
  /** The card has settings typed in and not saved. */
  dirty: boolean;
}) {
  const { t } = useTranslation();
  const bots = useBots();
  const stats = useChannelStats(channel.id, channel.status === "active");
  const check = useCheckChannel();
  const give = useGiveBot();
  const copy = useCopySettings();
  const [copyFrom, setCopyFrom] = useState("");
  const state = channelState(channel);
  // A channel is added switched off and stays so while it is being set up:
  // until its first post it is told what is left to do, not that it "is paused".
  const settingUp = state === "paused" && channel.last_post_at === null;
  const others = channels.filter((c) => c.id !== channel.id);

  const recheck = async () => {
    try {
      const out = await check.mutateAsync({ id: channel.id });
      pushToast(
        out.problem
          ? { kind: "warning", text: t(problemKeys(out.publisher, out.problem), out.problem) }
          : { kind: "success", text: t(byPublisher("aggregator.channel.check_ok")) },
      );
    } catch (err) {
      pushToast({ kind: "error", text: errorText(err, t("aggregator.action_error")) });
    }
  };

  // The account that runs the channel makes the bot its admin — again, or
  // another bot: nobody goes into Telegram to put a bot back.
  const giveBot = async (bot_id?: string) => {
    try {
      const out = await give.mutateAsync({ id: channel.id, bot_id });
      pushToast({
        kind: "success",
        text: t("aggregator.overview.given", { name: out.bot?.username ?? "" }),
      });
    } catch (err) {
      pushToast({ kind: "error", text: errorText(err, t("aggregator.action_error")) });
    }
  };
  const botless = channel.problem === "no_bot" || channel.problem === "token_invalid";

  const recheckButton = (
    <Button
      size="sm"
      variant="outline"
      loading={check.isPending}
      onClick={recheck}
      iconLeft={<RefreshCw size={14} strokeWidth={2} aria-hidden="true" />}
    >
      {t("aggregator.channel.check")}
    </Button>
  );

  return (
    <div className="space-y-6">
      {settingUp && (
        <Alert
          tone="info"
          title={t("aggregator.overview.new_title")}
          action={
            !canEdit ? undefined : channel.sources === 0 ? (
              <Button size="sm" onClick={() => onOpenTab("sources")}>
                {t("aggregator.overview.add_sources")}
              </Button>
            ) : onSwitchOn ? (
              <Button
                size="sm"
                loading={switching}
                onClick={onSwitchOn}
                iconLeft={<Play size={14} strokeWidth={2} aria-hidden="true" />}
              >
                {t("aggregator.overview.switch_on")}
              </Button>
            ) : undefined
          }
        >
          <ol className="list-decimal space-y-1 pl-5">
            <li>{t("aggregator.overview.new_step1")}</li>
            <li>{t("aggregator.overview.new_step2")}</li>
            <li>{t("aggregator.overview.new_step3")}</li>
            <li>{t("aggregator.overview.new_step4")}</li>
          </ol>
        </Alert>
      )}
      {state === "paused" && !settingUp && (
        <Alert tone="warning" title={t("aggregator.overview.paused_title")}>
          {t("aggregator.overview.paused_text")}
        </Alert>
      )}
      {state === "stopped" && (
        <Alert
          tone="danger"
          title={t(problemKeys(channel.publisher, channel.problem), channel.problem ?? "")}
          action={
            canEdit ? (
              <div className="flex flex-wrap gap-2">
                {channel.publisher === "bot" && channel.problem !== "not_a_channel" && (
                  <Button
                    size="sm"
                    loading={give.isPending}
                    onClick={() => giveBot()}
                    iconLeft={<Bot size={14} strokeWidth={2} aria-hidden="true" />}
                  >
                    {t(botless ? "aggregator.overview.give" : "aggregator.overview.give_back")}
                  </Button>
                )}
                {recheckButton}
              </div>
            ) : undefined
          }
        >
          <p>
            {t(problemKeys(channel.publisher, channel.problem, true), t("aggregator.problem_hint.other"))}
          </p>
          {channel.problem_detail && (
            <p className="mt-1 font-mono text-xs text-fg-muted">{channel.problem_detail}</p>
          )}
        </Alert>
      )}
      {state === "idle" && (
        <Alert
          tone="info"
          title={t("aggregator.overview.idle_title")}
          action={
            <Button size="sm" onClick={() => onOpenTab("sources")}>
              {t("aggregator.overview.add_sources")}
            </Button>
          }
        >
          {t("aggregator.overview.idle_text")}
        </Alert>
      )}
      {state === "working" && (
        <Alert tone="success" title={t("aggregator.overview.working_title")}>
          {channel.next_post_at
            ? t("aggregator.overview.next_post", {
                when: formatMoment(channel.next_post_at, channel.timezone),
              })
            : channel.review > 0
              ? t("aggregator.overview.waits_review")
              : t("aggregator.overview.waits_posts")}
        </Alert>
      )}
      {state !== "stopped" && channel.sources_broken > 0 && (
        <Alert
          tone="warning"
          action={
            <Button size="sm" variant="outline" onClick={() => onOpenTab("sources")}>
              {t("aggregator.overview.open_sources")}
            </Button>
          }
        >
          {t("aggregator.overview.sources_broken", { count: channel.sources_broken })}
        </Alert>
      )}

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Tile
          label={t("aggregator.overview.queued")}
          value={formatNumber(channel.queued + channel.pending)}
          note={
            channel.pending > 0
              ? t("aggregator.overview.pending_note", { n: formatNumber(channel.pending) })
              : undefined
          }
          onClick={() => onOpenTab("queue")}
        />
        <Tile
          label={t("aggregator.overview.review")}
          value={formatNumber(channel.review)}
          tone={channel.review > 0 ? "warning" : undefined}
          onClick={() => onOpenTab("review")}
        />
        <Tile
          label={t("aggregator.overview.published_24h")}
          value={formatNumber(channel.published_24h)}
          note={
            channel.last_post_at
              ? t("aggregator.overview.last_post", { when: formatRelative(channel.last_post_at) })
              : t("aggregator.overview.no_posts_yet")
          }
          onClick={() => onOpenTab("journal")}
        />
        <Tile
          label={t("aggregator.overview.failed_24h")}
          value={formatNumber(channel.failed_24h)}
          tone={channel.failed_24h > 0 ? "danger" : undefined}
          onClick={() => onOpenTab("journal")}
        />
        <Tile
          label={t("aggregator.overview.sources")}
          value={formatNumber(channel.sources)}
          tone={channel.sources_broken > 0 ? "warning" : undefined}
          onClick={() => onOpenTab("sources")}
        />
        <Tile
          label={t("aggregator.overview.mode")}
          value={<span className="text-base">{t(`aggregator.ai.mode_${channel.ai_mode}`)}</span>}
          note={t(`aggregator.moderation.${channel.moderation}`)}
          onClick={() => onOpenTab("processing")}
        />
      </div>

      <FillSection channel={channel} canEdit={canEdit} />

      <Section title={t("aggregator.stats.title")} hint={t("aggregator.stats.hint")}>
        {stats.data && stats.data.taken === 0 && (
          <p className="text-sm text-fg-muted">{t("aggregator.stats.empty")}</p>
        )}
        {stats.data && stats.data.taken > 0 && (
          <div className="space-y-4">
            <p className="text-sm text-fg-secondary">
              {t("aggregator.stats.totals", {
                taken: formatNumber(stats.data.taken),
                published: formatNumber(stats.data.published),
                skipped: formatNumber(stats.data.skipped),
                failed: formatNumber(stats.data.failed),
              })}
              {stats.data.ai_tokens > 0 &&
                ` ${t("aggregator.stats.tokens", { n: formatNumber(stats.data.ai_tokens) })}`}
            </p>
            {stats.data.by_reason.length > 0 && (
              <div>
                <div className="mb-1.5 text-xs font-medium text-fg-muted">
                  {t("aggregator.stats.by_reason")}
                </div>
                <ul className="space-y-1 text-sm">
                  {stats.data.by_reason.map((row) => (
                    <li key={`${row.reason}:${row.rule_id}`} className="flex items-baseline gap-3">
                      <span className="w-10 shrink-0 text-right font-mono tabular-nums text-fg-primary">
                        {row.count}
                      </span>
                      <span className="text-fg-secondary">{reasonText(t, row.reason, null)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {stats.data.by_source.length > 0 && (
              <div>
                <div className="mb-1.5 text-xs font-medium text-fg-muted">
                  {t("aggregator.stats.by_source")}
                </div>
                <ul className="space-y-1 text-sm">
                  {stats.data.by_source.map((row) => (
                    <li key={row.username} className="flex items-baseline gap-3">
                      <span className="w-16 shrink-0 text-right font-mono tabular-nums text-fg-primary">
                        {row.published}/{row.taken}
                      </span>
                      <span className="min-w-0 flex-1 truncate text-fg-secondary">
                        {row.title ?? `@${row.username}`}
                      </span>
                      {row.avg_views !== null && (
                        <span className="shrink-0 text-xs text-fg-muted">
                          {t("aggregator.stats.avg_views", { n: formatNumber(row.avg_views) })}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </Section>

      {channel.publisher === "account" ? (
        <Section
          title={t("aggregator.overview.publisher_title")}
          hint={t("aggregator.overview.publisher_hint")}
        >
          <div className="flex flex-wrap items-center gap-3">
            {channel.managers.length > 0 ? (
              <p className="flex-1 text-sm text-fg-secondary">
                {t("aggregator.overview.publisher_main", {
                  account: formatAccountNo(channel.managers[0]),
                })}
                {" · "}
                {channel.managers.length > 1
                  ? t("aggregator.overview.publisher_spare", {
                      accounts: channel.managers.slice(1).map(formatAccountNo).join(", "),
                    })
                  : t("aggregator.overview.publisher_no_spare")}
              </p>
            ) : (
              <p className="flex-1 text-sm text-danger">{t("aggregator.problem.no_account")}</p>
            )}
            {canEdit && state !== "stopped" && recheckButton}
          </div>
        </Section>
      ) : (
      <Section title={t("aggregator.overview.bot_title")}>
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[14rem] flex-1 space-y-1.5">
            <Label>{t("aggregator.channel.bot")}</Label>
            <SimpleSelect
              value={channel.bot?.id ?? ""}
              disabled={!canEdit || give.isPending}
              placeholder={t("aggregator.overview.no_bot")}
              onValueChange={(bot_id) => void giveBot(bot_id)}
              options={(bots.data ?? []).map((bot) => ({
                value: bot.id,
                label: `@${bot.username}`,
              }))}
            />
          </div>
          <Button variant="outline" onClick={() => onOpenConnections("bots")}>
            {t("aggregator.connections.open")}
          </Button>
          {canEdit && state !== "stopped" && recheckButton}
        </div>
      </Section>
      )}

      {canEdit && others.length > 0 && (
        <Section title={t("aggregator.copy.title")} hint={t("aggregator.copy.hint")}>
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-[14rem] flex-1">
              <SimpleSelect
                value={copyFrom}
                onValueChange={setCopyFrom}
                placeholder={t("aggregator.copy.pick")}
                options={others.map((c) => ({ value: c.id, label: c.title }))}
              />
            </div>
            <Button
              variant="outline"
              // With settings typed in and not saved the copy would land
              // under them — unseen, and overwritten by the next "Save".
              disabled={!copyFrom || copy.isPending || dirty}
              loading={copy.isPending}
              onClick={async () => {
                try {
                  await copy.mutateAsync({ id: channel.id, from_channel_id: copyFrom });
                  pushToast({ kind: "success", text: t("aggregator.copy.done") });
                  setCopyFrom("");
                } catch (err) {
                  pushToast({ kind: "error", text: errorText(err, t("aggregator.action_error")) });
                }
              }}
            >
              {t("aggregator.copy.run")}
            </Button>
          </div>
          {dirty && <p className="text-xs text-warning">{t("aggregator.copy.save_first")}</p>}
        </Section>
      )}
    </div>
  );
}
